import { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { invokeFunction } from '@/lib/cloudrun'; // Votre utilitaire Cloud Run personnalisé

/**
 * Définition exhaustive des tiers d'abonnement
 */
export type SubscriptionTier = 
  | 'visitor' 
  | 'essential' 
  | 'essential-plus' 
  | 'acquisition-pro'
  | 'acquisition_pro'
  | 'pro' 
  | 'byod' 
  | null;

interface SubscriptionState {
  tier: SubscriptionTier;
  subscribed: boolean;
  subscriptionEnd: string | null;
  loading: boolean;
  error: string | null;
  lastChecked: number | null;
}

/**
 * Hook de gestion des abonnements
 * Backend: Google Cloud Run (NestJS) avec fallback Firestore
 */
export function useSubscription() {
  const [state, setState] = useState<SubscriptionState>({
    tier: null,
    subscribed: false,
    subscriptionEnd: null,
    loading: true,
    error: null,
    lastChecked: null,
  });

  // Utilisation d'une ref pour éviter les boucles infinies sur l'intervalle de rafraîchissement
  const isCheckingRef = useRef(false);

  /**
   * Normalisation rigoureuse des chaînes de caractères pour les tiers
   * Gère les variations de casse et de formatage (ex: "Essential Plus" -> "essential-plus")
   */
  const normalizeTier = (value: unknown): SubscriptionTier => {
    if (value === undefined || value === null) return null;
    
    const str = String(value).trim().toLowerCase();
    const normalized = str.replace(/[_\s]+/g, '-');

    const validTiers: SubscriptionTier[] = ['visitor', 'essential', 'essential-plus', 'acquisition-pro', 'acquisition_pro', 'pro', 'byod'];
    
    // Mapping des alias communs
    if (normalized === 'essentialplus') return 'essential-plus';
    if (normalized === 'acquisitionpro') return 'acquisition-pro';
    if (normalized === 'free') return 'visitor';
    if (normalized === 'premium') return 'pro';

    return validTiers.includes(normalized as SubscriptionTier) 
      ? (normalized as SubscriptionTier) 
      : null;
  };

  /**
   * Stratégie de Fallback : Lecture directe dans Firestore
   * Vérifie les collections 'users' et 'userProfiles' par redondance
   */
  const getFallbackTier = async (userId: string): Promise<{ tier: SubscriptionTier; end: string | null }> => {
    console.warn(`[useSubscription] Initiating Firestore fallback for user: ${userId}`);
    try {
      const [usersSnap, profileSnap] = await Promise.all([
        getDoc(doc(db, 'users', userId)),
        getDoc(doc(db, 'userProfiles', userId)),
      ]);

      const uData = usersSnap.exists() ? usersSnap.data() : null;
      const pData = profileSnap.exists() ? profileSnap.data() : null;

      // Priorité aux données de la collection 'users'
      const rawTier = uData?.subscriptionTier || uData?.tier || pData?.subscriptionTier || pData?.tier;
      const rawEnd = uData?.subscriptionEnd || uData?.expiresAt || pData?.subscriptionEnd;

      // Conversion des Timestamps Firestore en ISO String
      let formattedEnd = null;
      if (rawEnd instanceof Timestamp) {
        formattedEnd = rawEnd.toDate().toISOString();
      } else if (rawEnd) {
        formattedEnd = new Date(rawEnd).toISOString();
      }

      return {
        tier: normalizeTier(rawTier),
        end: formattedEnd
      };
    } catch (err) {
      console.error('[useSubscription] Critical: Fallback Firestore lookup failed', err);
      return { tier: null, end: null };
    }
  };

  /**
   * Fonction principale de vérification
   * Interroge Cloud Run -> Si échec -> Interroge Firestore
   */
  const checkSubscription = useCallback(async (forceLoading = true) => {
    const user = auth.currentUser;
    
    if (!user) {
      setState(s => ({ ...s, tier: null, subscribed: false, loading: false }));
      return;
    }

    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    if (forceLoading) setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // 1. Tentative via Cloud Run
      console.log('[useSubscription] Calling Cloud Run function...');
      const response = await invokeFunction('check-subscription', { 
        userId: user.uid,
        email: user.email,
        timestamp: new Date().toISOString()
      });

      if (!response || response.error) {
        throw new Error(response?.error || 'Empty response from Cloud Run');
      }

      const resolvedTier = normalizeTier(response.tier);
      
      setState({
        tier: resolvedTier,
        subscribed: response.subscribed || (resolvedTier !== null && resolvedTier !== 'visitor'),
        subscriptionEnd: response.subscription_end || null,
        loading: false,
        error: null,
        lastChecked: Date.now(),
      });

    } catch (err) {
      console.error('[useSubscription] Primary check failed, switching to fallback strategy', err);

      // 2. Fallback Firestore si Cloud Run échoue (ex: Timeout ou 500)
      const fallback = await getFallbackTier(user.uid);

      setState({
        tier: fallback.tier,
        subscribed: fallback.tier !== null && fallback.tier !== 'visitor',
        subscriptionEnd: fallback.end,
        loading: false,
        error: `Cloud Run unavailable. Using cached Firestore data.`,
        lastChecked: Date.now(),
      });
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  /**
   * Effet : Synchronisation avec l'état Firebase Auth
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        checkSubscription();
      } else {
        setState({
          tier: null,
          subscribed: false,
          subscriptionEnd: null,
          loading: false,
          error: null,
          lastChecked: null
        });
      }
    });

    return () => unsubscribe();
  }, [checkSubscription]);

  /**
   * Effet : Rafraîchissement automatique
   * Utile pour détecter l'expiration d'une session ou la fin d'un abonnement en cours
   */
  useEffect(() => {
    if (!auth.currentUser) return;

    // Rafraîchir toutes les 5 minutes (300000 ms) pour éviter de surcharger Cloud Run
    // contrairement au 60s qui peut être agressif selon votre quota
    const interval = setInterval(() => {
      checkSubscription(false); // background refresh sans spinner de loading
    }, 300000);

    return () => clearInterval(interval);
  }, [checkSubscription]);

  return {
    ...state,
    refresh: () => checkSubscription(true),
    // Helpers pratiques pour les composants UI
    isPro: state.tier === 'pro',
    isEssential: state.tier === 'essential' || state.tier === 'essential-plus',
    isBYOD: state.tier === 'byod'
  };
}